import {
  Banner,
  Button,
  FlexBox,
  Text,
  Token,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import EmojiOne from 'emojione';
import { accessControl } from 'lib-shared-access-control';
import { ConversationFormValues } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { Field, FieldArray, reduxForm } from 'redux-form';
import isEmail from 'validator/lib/isEmail';

import { getChannelMessageMaxLength } from 'app/utils/conversation';
import { AppLink } from 'components/appLink';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { CopilotAction } from 'components/copilotDropdown';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { ExpandText } from 'components/copilotPinnedActions/components/expandText';
import { ImproveReply } from 'components/copilotPinnedActions/components/improveReply';
import { MatchToStyleGuide } from 'components/copilotPinnedActions/components/matchToStyleGuide';
import { QuickTranslate } from 'components/copilotPinnedActions/components/quickTranslate';
import { SpellingAndGrammar } from 'components/copilotPinnedActions/components/spellingAndGrammar';
import {
  DEFAULT_PINNED_STATE,
  EditorCopilotDropdown,
} from 'components/editorCopilotDropdown';
import {
  EditorCopilotDropdownFeedback,
  EditorCopilotDropdownImproveReply,
  EditorCopilotDropdownMatchToStyleGuide,
  EditorCopilotDropdownSpellingAndGrammar,
  EditorCopilotDropdownTranslate,
  EditorCopilotDropdownTranslateToConversationLanguage,
} from 'components/editorCopilotDropdown/components';
import { EditorCopilotDropdownExpandText } from 'components/editorCopilotDropdown/components/expandText/copilotDropdownExpandText';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
import { AttachmentsFieldList } from 'components/shared/util/richTextField/components/components/attachmentsFieldList/attachmentsFieldList';
import { ValidationState } from 'components/shared/util/richTextField/components/components/stateBar/stateBar.types';
import { copilotUtils } from 'utils/copilot';
import { getEmailIntegrationLabel } from 'utils/emailIntegration';
import { ServerStorageProps } from 'utils/useServerStorage';

import { getRequestID } from '../../shared/util/request';
import RichTextField, {
  RichTextFieldRef,
} from '../../shared/util/richTextField/components/richTextField';
import turndown from '../../shared/util/richTextField/components/utils/turndown-service';
import { ConversationFormInjectedProps } from '../containers/conversationForm';
import {
  getConversationFormEmailInitialValues,
  getConversationFormName,
  getUniqueRecipientEmails,
} from '../utils/conversationForm';
import { validateMessageSubmission } from '../utils/validateMessageSubmission';

import ListUserEmailOptionsCcBcc from './ListUserEmailOptionsCcBcc';
import UserItem from './UserItem';
import { ReplyAllButton } from './replyAll/replyAll';
import { getLastReviewReply } from './utils/trustpilotConversationReview';

import './../styles/conversationForm.less';

type PropsT = {
  className: string;
  tempMessage?: {
    text: string;
    content_type: 'html' | 'markdown';
  } | null;
  formValues?: ConversationFormValues;
  handleSubmit: (payload: any) => any;
  isPublicReply?: boolean;
  hasIntegrationSetupAccess: boolean;
  hasCopilot: boolean;
  csid: string;
} & ConversationFormInjectedProps &
  ServerStorageProps<'organization', 'organizationStorage'>;

type StateT = {
  bccVisible: boolean;
  ccVisible: boolean;
  fileStatuses: any;
  validation: ValidationState;
  isUploadingAttachments: boolean;
  editorText: string;
  hasStyleGuideForChannel: boolean;
  editorCopilotActions: Record<string, CopilotAction>;
};

const markdownReplacements = [
  [/\\/g, '\\\\'],
  [/\*/g, '\\*'],
  [/^-/g, '\\-'],
  [/^\+ /g, '\\+ '],
  [/^(=+)/g, '\\$1'],
  [/^(#{1,6}) /g, '\\$1 '],
  [/`/g, '\\`'],
  [/^~~~/g, '\\~~~'],
  [/\[/g, '\\['],
  [/\]/g, '\\]'],
  [/^>/g, '\\>'],
  [/^(\d+)\. /g, '$1\\. '],
];

const escapeMarkdown = (text) =>
  markdownReplacements.reduce(
    (search, replacement) => search.replace(replacement[0], replacement[1]),
    text,
  );

const validate = (values, props) => {
  const errors: any = {};

  if (isEmpty(values)) {
    return errors;
  }

  if (
    props.metaConversation.conversation.initial_channel === 'email' ||
    props.metaConversation.conversation.initial_channel === 'contactform'
  ) {
    if (
      props.getAllFromAsyncStatus === 'succeeded' &&
      (!props.fromEmails || props.fromEmails.length === 0)
    ) {
      if (props.pristine === false) {
        const message = props.hasIntegrationSetupAccess
          ? `You do not have any email integration. Please add one at [settings/email](/settings/email).`
          : `You do not have any email integration yet. Ask your administrator to add one.`;
        const options = {
          title: 'You do not have an email in Dixa yet',
          text: message,
          className: 'danger',
          icon: 'i-exclamation',
        };
        props.notificationAdd(options);
      }

      errors._error = 'Please add an email.';
    } else if (!values.fromEmail) {
      errors.fromEmail = { _error: 'Required' };
    } else if (values.fromEmail && isEmail(values.fromEmail) === false) {
      errors.fromEmail = { _error: 'From email is not valid' };
    } else if (!values.to?.length) {
      errors.to = { _error: 'Required' };
    }
  }
  return errors;
};

const findLastMessage = (conversation) => {
  const events = conversation?.events || [];
  const messages = events.filter(
    (event) =>
      event.type === 'conversation:message_added' &&
      event.direction === 'inbound',
  );

  if (messages.length === 0) return '';
  if (!messages[messages.length - 1].text) return '';
  return messages[messages.length - 1].text as string;
};

const getEditorCopilotOptions = (
  metaConversation,
  hasStyleGuideForChannel: boolean,
): Record<string, CopilotAction> => {
  return {
    improveReply: {
      title: 'Improve reply',
      icon: 'pen',
      commandName: 'richTextEditor:improveReply',
      content: (
        <EditorCopilotDropdownImproveReply
          channel={metaConversation.conversation.initial_channel}
          lastMessage={findLastMessage(metaConversation.conversation)}
          hasStyleGuideForChannel={hasStyleGuideForChannel}
          csid={metaConversation.conversation.id}
        />
      ),
      isPinned: ({ pinnedState }) =>
        pinnedState?.improveReply ?? DEFAULT_PINNED_STATE.improveReply,
    },
    improveReplyMatchStyleGuide: {
      title: 'Match to style guide',
      icon: 'widget-customize',
      commandName: 'richTextEditor:matchToStyleGuide',
      content: (
        <EditorCopilotDropdownMatchToStyleGuide
          channel={metaConversation.conversation.initial_channel}
          lastMessage={findLastMessage(metaConversation.conversation)}
          csid={metaConversation.conversation.id}
        />
      ),
      isDisabled: ({ currentEditorText }) => {
        const { isDisabled, tooltipContent } =
          copilotUtils.matchToStyleGuide.getStatus({
            text: currentEditorText || '',
            channel: metaConversation.conversation.initial_channel,
            hasStyleGuideForChannel,
          });

        if (isDisabled) {
          return tooltipContent;
        }

        return false;
      },
      isPinned: ({ pinnedState }) =>
        pinnedState?.improveReplyMatchStyleGuide ??
        DEFAULT_PINNED_STATE.improveReplyMatchStyleGuide,
    },
    translateToConversationLanguage: {
      title: "Quick translate to conversation's language",
      icon: 'translate-auto',
      commandName: 'richTextEditor:translateToConversationLanguage',
      content: (
        <EditorCopilotDropdownTranslateToConversationLanguage
          languageCode={
            metaConversation?.conversation?.attributes?.language ?? ''
          }
          csid={metaConversation.conversation.id}
        />
      ),
      isDisabled: ({ currentEditorText }) => {
        const hasLanguage =
          !!metaConversation?.conversation?.attributes?.language;
        const { isDisabled, tooltipContent } =
          copilotUtils.quickTranslate.getStatus({
            text: currentEditorText || '',
            hasLanguage,
          });
        if (isDisabled) {
          return tooltipContent;
        }
        return false;
      },
      isPinned: ({ pinnedState }) =>
        pinnedState?.translateToConversationLanguage ??
        DEFAULT_PINNED_STATE.translateToConversationLanguage,
    },
    translate: {
      title: 'Translate',
      icon: 'translate',
      commandName: 'richTextEditor:translate',
      content: <EditorCopilotDropdownTranslate />,
    },
    checkSpellingAndGrammar: {
      title: 'Fix spelling and grammar',
      commandName: 'richTextEditor:checkSpellingAndGrammar',
      icon: 'grammar-spelling',
      content: <EditorCopilotDropdownSpellingAndGrammar />,
      isPinned: ({ pinnedState }) =>
        pinnedState?.checkSpellingAndGrammar ??
        DEFAULT_PINNED_STATE.checkSpellingAndGrammar,
      isDisabled: ({ currentEditorText }) => {
        const { isDisabled, tooltipContent } =
          copilotUtils.spellingAndGrammar.getStatus({
            text: currentEditorText || '',
          });

        if (isDisabled) {
          return tooltipContent;
        }

        return false;
      },
    },

    expandText: {
      title: 'Expand reply',
      commandName: 'richTextEditor:expandText',
      icon: 'chat-plus',
      content: (
        <EditorCopilotDropdownExpandText
          channel={metaConversation.conversation.initial_channel}
          lastMessage={findLastMessage(metaConversation.conversation)}
          csid={metaConversation.conversation.id}
        />
      ),
      isPinned: ({ pinnedState }) =>
        pinnedState?.expandText ?? DEFAULT_PINNED_STATE.expandText,
      isDisabled: ({ currentEditorText }) => {
        const { isDisabled, tooltipContent } =
          copilotUtils.expandText.getStatus({
            text: currentEditorText || '',
          });

        if (isDisabled) {
          return tooltipContent;
        }

        return false;
      },
    },
    sendFeedback: {
      title: 'Send feedback',
      icon: 'paper-plane',
      hasDividerBefore: true,
      content: <EditorCopilotDropdownFeedback />,
    },
  } as const satisfies Record<string, CopilotAction>;
};

type TextWithContentType = {
  text: string;
  contentType: 'markdown' | 'html';
};

class ConversationForm extends React.Component<PropsT, StateT> {
  newEditorRef: React.RefObject<RichTextFieldRef | null>;
  timeouts: NodeJS.Timeout[];

  constructor(props: PropsT) {
    super(props);
    this.timeouts = [];

    turndown.escape = escapeMarkdown;

    this.state = {
      bccVisible: false,
      ccVisible: false,
      fileStatuses: {},
      validation: { touched: false, valid: false },
      isUploadingAttachments: false,
      editorText: '',
      hasStyleGuideForChannel: false,
      editorCopilotActions: getEditorCopilotOptions(
        this.props.metaConversation,
        false,
      ),
    };
    this.newEditorRef = React.createRef<RichTextFieldRef | null>();

    this.handleValidatedMessageOnSendClick =
      this.handleValidatedMessageOnSendClick.bind(this);
    this.renderElementItem = this.renderElementItem.bind(this);
    this.reportAgentTyping = debounce(this.reportAgentTyping.bind(this), 500, {
      leading: true,
      trailing: false,
    });
  }

  async componentDidMount() {
    const { metaConversation, tempMessage } = this.props;

    /**
     * Conceptually, trustpilotreviews reply should be edit only. Hence if reply
     * already exist, copy the last one into the form to simplify editing. Or
     * use the last tempMessage.
     */
    if (metaConversation.conversation.initial_channel === 'trustpilotreviews') {
      const reply = getLastReviewReply(metaConversation);
      if (reply?.text || tempMessage?.text) {
        const text = tempMessage?.text.trim() ? tempMessage?.text : reply?.text;
        this.setEditorMarkdown(text);
        this.focusEditor();
      }
    } else if (metaConversation.isAssignedToMe) {
      if (tempMessage) {
        if (tempMessage.contentType === 'markdown') {
          this.setEditorMarkdown(tempMessage.text);
        } else {
          this.setEditorText(tempMessage.text);
        }
      }

      this.focusEditor();
    }

    this.initializeForm();

    const hasStyleGuideForChannel = await this.props.hasStyleGuideForChannel(
      metaConversation.conversation.initial_channel,
    );

    this.setState({
      hasStyleGuideForChannel,
      editorCopilotActions: getEditorCopilotOptions(
        metaConversation,
        hasStyleGuideForChannel,
      ),
    });
  }

  componentWillUnmount() {
    this.saveTempFormValuesAndMessage();

    if (this.timeouts !== null) {
      for (const timeout of this.timeouts) {
        clearTimeout(timeout);
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: PropsT) {
    const propsLiveAssessment =
      this.props.metaConversation.conversation.live_assesment;
    const propsEndUserIsNotConnected =
      propsLiveAssessment &&
      propsLiveAssessment.is_end_user_connected === false;
    const nextPropsLiveAssessment =
      nextProps.metaConversation.conversation.live_assesment;
    const nextPropsEndUserIsNotConnected =
      nextPropsLiveAssessment &&
      nextPropsLiveAssessment.is_end_user_connected === false;

    if (
      nextProps.metaConversation.conversation.initial_channel ===
        'widgetchat' &&
      nextProps.metaConversation.isAssignedToMe === true &&
      nextPropsEndUserIsNotConnected === true &&
      nextProps.metaConversation.conversation.status !== 'closed'
    ) {
      this.setValidationState({
        touched: true,
        valid: false,
        error: this.state.validation.error,
        warning: { type: 'offline' },
      });
    } else if (
      nextProps.metaConversation.conversation.initial_channel ===
        'widgetchat' &&
      nextProps.metaConversation.isAssignedToMe === true &&
      propsEndUserIsNotConnected === true &&
      nextPropsEndUserIsNotConnected === false &&
      nextProps.metaConversation.conversation.status !== 'closed'
    ) {
      this.setValidationState({ touched: false, valid: false });
    }

    // If the conversation is going from open to closed and we need to reset the tempMessage.
    if (
      nextProps.metaConversation.conversation.is_closed === true &&
      this.props.metaConversation.conversation.is_closed === false
    ) {
      // We allow to reply back to closed email conversations.
      if (
        this.props.metaConversation.conversation.initial_channel !== 'email'
      ) {
        this.resetFormState(); // Clear the state.
        this.props.initialize({}); // Clear the to, from, cc and bcc.
      }
    }
    if (nextProps.hasCopilot) {
      if (
        nextProps.elevioSmartReplyData?.response && // Has valid incoming response
        this.props.elevioSmartReplyData !== nextProps.elevioSmartReplyData
      ) {
        const currentText = this.getEditorText();
        this.setEditorText(
          `<p>${currentText}<br/>${(
            nextProps.elevioSmartReplyData?.response as unknown as string
          ).replace(/\n/g, '<br/>')}</p>`,
        );
      }
      if (
        nextProps.knowledgeSmartReply.message && // Has valid incoming message
        this.props.knowledgeSmartReply !== nextProps.knowledgeSmartReply
      ) {
        const currentText = this.getEditorText();
        this.setEditorText(
          `<p>${currentText}<br/>${nextProps.knowledgeSmartReply.message.replace(
            /\n/g,
            '<br/>',
          )}</p>`,
        );
      }
    }
  }

  componentDidUpdate(prevProps: PropsT) {
    const { metaConversation, fromEmails, formValues } = this.props;

    const hasNewConversationId =
      metaConversation.conversation.id !==
      prevProps.metaConversation.conversation.id;
    const areEventsRecentlyLoaded =
      prevProps.metaConversation.conversation.events.length === 0 &&
      metaConversation.conversation.events.length > 0;
    const areEmailsRecentlyLoaded =
      prevProps.fromEmails.length === 0 && fromEmails.length > 0;
    const isEmailBasedChannel = ['email', 'contactform'].includes(
      metaConversation.conversation.initial_channel ?? '',
    );

    /**
     * (Re)initialize the form when events are populated. Events are populated
     * at later stage than csid and that is why we need the componentDidUpdate
     * lifecycle method instead of only componentDidMount.
     */
    if (
      isEmailBasedChannel &&
      isEmpty(formValues) &&
      (hasNewConversationId ||
        areEventsRecentlyLoaded ||
        areEmailsRecentlyLoaded)
    ) {
      this.initializeForm();
    }
  }

  /**
   * Saves the message and form values in the store so that in case the agent
   * leaves the current conversation and comes back, we can retrieve form
   * state.
   */
  saveTempFormValuesAndMessage() {
    const {
      metaConversation,
      formValues,
      saveTempMessage,
      conversationSaveTempFormValues,
    } = this.props;

    saveTempMessage(
      metaConversation.conversation.id,
      this.getMessageTextAndContentType(),
    );

    if (!isEmpty(formValues)) {
      conversationSaveTempFormValues(
        metaConversation.conversation.id,
        formValues,
      );
    }
  }

  initializeForm() {
    const {
      metaConversation: { conversation },
      tempFormValues,
      fromEmails,
      initialize,
      change,
      organizationStorage: { data: organizationStorage },
      hasFFReplyAll,
    } = this.props;
    const initialValues = getConversationFormEmailInitialValues(
      conversation,
      fromEmails,
    );
    initialize({
      ...initialValues,
      // If the organization setting is set to 'reply', we clear make sure there is only 1 to selected on form init
      ...(hasFFReplyAll &&
        organizationStorage.email_reply_all === 'reply' &&
        initialValues.to && {
          to: [initialValues.to[0]],
        }),
      // If the organization setting is set to 'reply', we clear CC field on form init
      ...(hasFFReplyAll &&
        organizationStorage.email_reply_all === 'reply' && { cc: [] }),
    });

    if (tempFormValues) {
      Object.entries(tempFormValues).map(([field, value]) => {
        change(field, value);
      });
    }
  }

  getEditorText() {
    if (this.newEditorRef && this.newEditorRef.current) {
      const { current: newEditor } = this.newEditorRef;
      return newEditor.getText();
    }
    return '';
  }

  setEditorText(newText: string) {
    const editor = this.newEditorRef?.current;
    if (editor && typeof editor.setText === 'function') {
      editor.setText(newText);
      if (newText) {
        editor.moveCursorToEnd();
      }
    }
  }

  setEditorMarkdown(newText: string) {
    const editor = this.newEditorRef?.current;
    if (editor && typeof editor.setMarkdown === 'function') {
      editor.setMarkdown(newText);
      if (newText) {
        editor.moveCursorToEnd();
      }
    }
  }

  resetFormState() {
    const {
      reset,
      conversationSaveTempFormValues,
      saveTempMessage,
      metaConversation: { conversation },
    } = this.props;
    this.setEditorText('');
    reset();
    conversationSaveTempFormValues(conversation.id, null);
    saveTempMessage(conversation.id, null);
  }

  focusEditor() {
    this.timeouts.push(
      setTimeout(() => {
        if (this.newEditorRef && this.newEditorRef.current) {
          this.newEditorRef.current.focus();
        }
      }),
    );
  }

  setValidationState = (validation: ValidationState) => {
    this.setState({ validation });
  };

  handleFileIsUploadingChange = (isUploadingAttachments: boolean) => {
    this.setState({ isUploadingAttachments });
  };

  adaptToMarkedFormat = (markdown: string) => {
    const lines = markdown.split('\n');
    const linesWithNewlineAfterQuotedLines = lines.reduce((acc: any, line) => {
      if (line.startsWith('>')) return acc.concat([line, '\n']);
      return acc.concat([line]);
    }, []);

    return linesWithNewlineAfterQuotedLines.join('\n');
  };

  getMessageTextAndContentType(): TextWithContentType {
    const { hasFFWriteHTMLEmail } = this.props;
    const editor = this.newEditorRef.current;
    const channel = this.props.metaConversation.conversation.initial_channel;

    if (channel === 'whatsapp') {
      return {
        text: turndown.turndown(editor?.getText().trim() || ''),
        contentType: 'markdown',
      };
    }

    if (['sms', 'facebookmessenger', 'trustpilotreviews'].includes(channel)) {
      return {
        text: editor?.getPlainText().trim() || '',
        contentType: 'markdown',
      };
    }

    if (hasFFWriteHTMLEmail && ['email', 'contactform'].includes(channel)) {
      return {
        text: editor?.getText().trim() || '',
        contentType: 'html',
      };
    }

    if ('messenger' === channel) {
      return {
        text: EmojiOne.shortnameToUnicode(editor?.getText().trim() || ''),
        contentType: 'html',
      };
    }

    return {
      text: turndown.turndown(editor?.getText().trim() || ''),
      contentType: 'markdown',
    };
  }

  isMaxLengthExceeded(text: string) {
    const initialChannel =
      this.props.metaConversation.conversation.initial_channel;
    const isPublicReply =
      this.props.metaConversation.conversation.isPublicReply;
    const channelMaxMessageLength = getChannelMessageMaxLength(
      initialChannel,
      isPublicReply,
    );

    return text.length > channelMaxMessageLength;
  }

  async handleValidatedMessageOnSendClick(values: any) {
    const { text, contentType } = this.getMessageTextAndContentType();

    const validation = validateMessageSubmission({
      text,
      channel: this.props.metaConversation.conversation.initial_channel,
      attachments: this.props.formValues?.attachments,
      isMaxLengthExceeded: (t) => this.isMaxLengthExceeded(t),
    });

    if (!validation.canSubmit) {
      this.setValidationState({
        touched: true,
        valid: false,
        error: { type: validation.errorType },
      });
      return;
    }

    this.setValidationState({ touched: false, valid: false });

    if (this.props.isPublicReply && validation.submissionType === 'text') {
      const { isCancelled } = await this.props.showPublicReplyConfirmModal();

      if (isCancelled) {
        return;
      }
    }

    const messageText = validation.submissionType === 'text' ? text : '';
    this.preparePayloadAndSubmitMessage(values, messageText, contentType);
    this.resetFormState();
  }

  parseAttachments(
    rawAttachments: any,
  ): { pretty_name: string; url: string }[] {
    // Attachment
    const attachments: { pretty_name: string; url: string }[] = [];
    if (
      rawAttachments !== undefined &&
      Array.isArray(rawAttachments) === true
    ) {
      for (const attachment of rawAttachments) {
        if (attachment !== undefined) {
          attachments.push({
            pretty_name: attachment.name,
            url: attachment.relative_uri,
          });
        }
      }
    }
    return attachments;
  }

  preparePayloadAndSubmitMessage(
    coversationValues: any,
    text: string,
    content_type: string,
  ) {
    const requestID = getRequestID();
    const payload: any = {
      requestID: requestID,
      frontend_id: requestID,
      message: text,
      content_type,
      csid: this.props.metaConversation.conversation.id,
      hasReceivedResponse: false,
      type: 'conversation:add_message',
      channel: this.props.metaConversation.conversation.initial_channel,
      author: this.props.profile,
      direction: 'outbound',
    };

    if (
      this.props.metaConversation.conversation.initial_channel ===
        'contactform' ||
      this.props.metaConversation.conversation.initial_channel === 'email'
    ) {
      payload.from = coversationValues.fromEmail;
      payload.cc_list_v2 = getUniqueRecipientEmails(coversationValues.cc);
      payload.bcc_list_v2 = getUniqueRecipientEmails(coversationValues.bcc);
      payload.to_emails = getUniqueRecipientEmails(coversationValues.to);
    }

    const attachments = this.parseAttachments(coversationValues.attachments);
    if (attachments.length > 0) {
      payload.attachments_v2 = attachments;
    }
    this.props.conversationSubmitMessage(payload).then(() => {
      ampli.conversationReplied({
        language:
          this.props.metaConversation.conversation?.attributes?.language,
        tags: this.props.metaConversation.conversation?.tags,
        channel: this.props.metaConversation.conversation?.initial_channel,
      });
    });
  }

  handleTypingEvent = (editorText: string) => {
    this.setState({ editorText });
    this.reportAgentTyping();
  };

  reportAgentTyping = () => {
    this.props.wsRequest({
      type: 'conversation:typing',
      params: {
        csid: this.props.metaConversation.conversation.id,
        channel: this.props.metaConversation.conversation.initial_channel,
      },
    });
  };

  toggleCC = () => {
    this.setState({ ccVisible: !this.state.ccVisible });
  };

  toggleBCC = () => {
    this.setState({ bccVisible: !this.state.bccVisible });
  };

  hideField = (targetName: string) => {
    if (targetName === 'cc') {
      this.setState({ ccVisible: true });
    } else if (targetName === 'bcc') {
      this.setState({ bccVisible: true });
    }
  };

  getToAddress() {
    const { metaConversation, formValues } = this.props;
    let toEmail = '';
    let contactName = '';

    // First we check that the formValues has a "to" address.
    const uniqueTo = getUniqueRecipientEmails(formValues?.to ?? undefined);
    if (uniqueTo?.[0]?.email) {
      toEmail = uniqueTo[0].email;
      contactName = uniqueTo[0].name ?? '';
      // We add the format name<email> if we also have a name.
      if (contactName) {
        toEmail = ` <${toEmail}>`;
      }
    } else {
      // For backwards compatibility we need to check attributes.to_email and attributes.from_email.
      if (
        metaConversation.conversation.attributes &&
        (metaConversation.conversation.attributes.to_email ||
          metaConversation.conversation.attributes.from_email)
      ) {
        toEmail =
          metaConversation.conversation.direction === 'inbound'
            ? metaConversation.conversation.attributes.from_email
            : metaConversation.conversation.attributes.to_email;
      }

      if (metaConversation.requester && metaConversation.requester.name) {
        contactName = metaConversation.requester.name;
        if (toEmail) {
          toEmail = ` <${toEmail}>`;
        }
      }
    }

    return contactName + toEmail;
  }

  renderElementItem(props: any) {
    return <UserItem {...props} />;
  }

  getRequesterName(): string | undefined {
    return this.props.metaConversation.requester?.name;
  }

  getRequesterHandleOrFallbackToName(): string | undefined {
    const handle =
      this.props.metaConversation.conversation?.additional_data
        ?.end_user_contact_point?.attributes?.screen_name;
    return handle ? `@${handle}` : this.getRequesterName();
  }

  render() {
    let noEmailIntegration: JSX.Element | null = null;
    let showSubmitButton = false;
    let showTextAddresses = false;
    let disableSubmit = this.props.invalid;
    let submitBtnTooltip: React.ReactNode = (
      <CommandKeybindingsTooltipContent commandName="richTextEditor:send">
        Send
      </CommandKeybindingsTooltipContent>
    );

    const {
      composeEmailMeta,
      composeEmailFindUser,
      fromEmails,
      users,
      handleSubmit,
      touch,
      metaConversation,
      personalSettings,
      formValues,
      asyncStatus,
    } = this.props;

    const isReviewWithReply = !!getLastReviewReply(metaConversation);
    let submitBtnText = isReviewWithReply ? 'Edit reply' : 'Send';

    const {
      isAssignedToMe,
      conversation: {
        is_closed: conversationIsClosed,
        initial_channel: convInitialChannel,
        id: csid,
      },
    } = metaConversation;

    if (
      asyncStatus.submit === 'loading' ||
      asyncStatus.close === 'loading' ||
      asyncStatus.getConversation === 'loading'
    ) {
      disableSubmit = true;
      submitBtnText = 'Loading';
    } else if (this.state.isUploadingAttachments) {
      disableSubmit = true;
      submitBtnText = 'Uploading';
      submitBtnTooltip = 'Uploading file attachments';
    } else if (this.props.isPublicReply) {
      submitBtnText = 'Send public reply';
    }

    if (this.props.asyncStatus.getConversation !== 'loading') {
      if (
        [
          'widgetchat',
          'messenger',
          'adachat',
          'digitalgeniuschat',
          'kindlychat',
          'genericapimessaging',
        ].includes(convInitialChannel) &&
        isAssignedToMe &&
        conversationIsClosed === false
      ) {
        if (personalSettings.submitMessageWithEnter === false) {
          showSubmitButton = true;
        }
      } else if (
        ['contactform', 'email'].includes(convInitialChannel) &&
        isAssignedToMe
      ) {
        showSubmitButton = true;
        showTextAddresses = true;
      } else if (
        [
          'facebookmessenger',
          'whatsapp',
          'sms',
          'twitter',
          'trustpilotreviews',
        ].includes(convInitialChannel) &&
        isAssignedToMe
      ) {
        if (personalSettings.submitMessageWithEnter === false) {
          showSubmitButton = true;
        }
      }
    }

    if (
      fromEmails.length === 0 &&
      composeEmailMeta.getAllFrom === 'succeeded'
    ) {
      noEmailIntegration = (
        <Banner
          variant="warning"
          icon={null}
          description={
            <Text.Paragraph as="div">
              No email integration found.{' '}
              {this.props.hasIntegrationSetupAccess && (
                <Text.ExternalLink>
                  <AppLink path="/settings/email">
                    Create one in email settings
                  </AppLink>
                </Text.ExternalLink>
              )}
            </Text.Paragraph>
          }
        />
      );
    }

    const fromEmailOptions = fromEmails.map((fromEmail) => {
      return {
        value: fromEmail.email_address,
        label: getEmailIntegrationLabel(fromEmail),
      };
    });

    const rootClass = classnames(
      {
        'conversation-form': true,
      },
      this.props.className,
    );

    const toggleCCButtonText = (this.state.ccVisible ? '-' : '+') + 'Cc';
    const toggleBCCButtonText = (this.state.bccVisible ? '-' : '+') + 'Bcc';

    const hasReplyAccess = accessControl.hasAccess('conversation.reply');

    if (!hasReplyAccess) {
      return (
        <FlexBox justifyContent="center" alignItems="center" flex="1">
          <Text.Paragraph variant="1" subdued>
            You don&apos;t have permission to respond to conversation
          </Text.Paragraph>
        </FlexBox>
      );
    }

    return (
      <form
        className={rootClass}
        name="conversation-form"
        onSubmit={handleSubmit((values) =>
          this.handleValidatedMessageOnSendClick(values),
        )}
      >
        {showTextAddresses && (
          <div className="cc-bcc-wrapped-area">
            {noEmailIntegration}
            <span className="cc-bcc-container">
              <ReplyAllButton conversation={metaConversation.conversation} />
              <Button type="button" variant="tertiary" onClick={this.toggleCC}>
                {toggleCCButtonText}
              </Button>
              <Button type="button" variant="tertiary" onClick={this.toggleBCC}>
                {toggleBCCButtonText}
              </Button>
            </span>
            <div className="cc-bcc-row">
              <Field
                primary
                label="From"
                required={true}
                options={fromEmailOptions}
                defaultOption="Please select a from email"
                name="fromEmail"
                component={Token}
              />

              <FieldArray
                name="to"
                required={true}
                singleChoice={false}
                fieldLabel="To"
                touch={touch}
                change={this.props.change}
                showSearch={true}
                component={ListUserEmailOptionsCcBcc}
                elementItem={this.renderElementItem}
                availableOptions={users}
                status={composeEmailMeta.findUser}
                getfunction={composeEmailFindUser}
                placeholder="Search for recipients..."
              />
            </div>
            <div className="cc-bcc-row">
              <FieldArray
                name="cc"
                required={false}
                singleChoice={false}
                fieldLabel="Cc"
                touch={touch}
                showSearch={this.state.ccVisible}
                hideField={this.hideField}
                change={this.props.change}
                component={ListUserEmailOptionsCcBcc}
                elementItem={this.renderElementItem}
                availableOptions={users}
                status={composeEmailMeta.findUser}
                getfunction={composeEmailFindUser}
                placeholder="Search for Cc recipients..."
              />
              <FieldArray
                name="bcc"
                required={false}
                singleChoice={false}
                fieldLabel="Bcc"
                touch={touch}
                showSearch={this.state.bccVisible}
                hideField={this.hideField}
                change={this.props.change}
                component={ListUserEmailOptionsCcBcc}
                elementItem={this.renderElementItem}
                availableOptions={users}
                status={composeEmailMeta.findUser}
                getfunction={composeEmailFindUser}
                placeholder="Search for Bcc recipients..."
              />
            </div>
          </div>
        )}

        <div className="conversation-form__editorwrap">
          <RichTextField
            className="conversation-form__editornew"
            ref={this.newEditorRef as any}
            onTriggerSend={handleSubmit(this.handleValidatedMessageOnSendClick)}
            onChange={this.handleTypingEvent}
            channel={metaConversation.conversation.initial_channel}
            requesterName={
              this.props.isPublicReply
                ? this.getRequesterHandleOrFallbackToName()
                : this.getRequesterName()
            }
            attachments={formValues?.attachments}
            attachmentsFields={
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <FieldArray name="attachments" component={AttachmentsFieldList} />
            }
            validation={this.state.validation}
            setValidationState={this.setValidationState}
            onFileIsUploadingChange={this.handleFileIsUploadingChange}
            form={getConversationFormName(csid)}
            hasBorder={false}
            isPublicReply={this.props.isPublicReply}
            toolbarAddons={
              <>
                <EditorQuickRepliesDropdown
                  conversationId={csid}
                  locale={metaConversation.conversation.attributes.language}
                  outboundMessages={
                    metaConversation.conversation.attributes
                      ?.outbound_message_count
                      ? parseInt(
                          metaConversation.conversation.attributes
                            .outbound_message_count,
                          10,
                        )
                      : 0
                  }
                  channel={metaConversation.conversation.initial_channel}
                  conversation={metaConversation.conversation}
                  popoverContent={{
                    align: 'start',
                  }}
                />
                <EditorCopilotDropdown
                  editorCopilotActions={this.state.editorCopilotActions}
                  channel={metaConversation.conversation.initial_channel}
                />
                <CopilotPinnedActions
                  channel={metaConversation.conversation.initial_channel}
                  languageCode={
                    metaConversation.conversation.attributes.language
                  }
                  text={this.getEditorText()}
                  csid={csid}
                  pinnableActions={[
                    {
                      id: 'improveReply',
                      component: ImproveReply,
                      condition: (args) =>
                        args.flags.agentCopilotPinActions &&
                        args.pinState['improveReply'],
                    },
                    {
                      id: 'improveReplyMatchStyleGuide',
                      component: MatchToStyleGuide,
                      condition: (args) =>
                        args.flags.agentCopilotPinActions &&
                        args.pinState['improveReplyMatchStyleGuide'],
                    },
                    {
                      id: 'translateToConversationLanguage',
                      component: QuickTranslate,
                      condition: (args) =>
                        args.pinState['translateToConversationLanguage'],
                    },
                    {
                      id: 'checkSpellingAndGrammar',
                      component: SpellingAndGrammar,
                      condition: (args) =>
                        args.flags.agentCopilotPinActions &&
                        args.pinState['checkSpellingAndGrammar'],
                    },
                    {
                      id: 'expandText',
                      component: ExpandText,
                      condition: (args) =>
                        args.flags.agentCopilotPinActions &&
                        args.pinState['expandText'],
                    },
                  ]}
                />
              </>
            }
          />
        </div>
        {showSubmitButton && (
          <div className="conversation-form__submit">
            <Tooltip content={submitBtnTooltip} wrap="div">
              <Button type="submit" disabled={disableSubmit}>
                {submitBtnText}
              </Button>
            </Tooltip>
          </div>
        )}
      </form>
    );
  }
}

export default reduxForm({
  validate: validate,
})(ConversationForm);
